import { useMemo, useState } from "react";
import { SOURCE_TYPE, sourceMeta } from "../utils/dataSource";
import { useClientStore } from "../hooks/useClientStore";
import { useHoldingsStore } from "../hooks/useHoldingsStore";
import { useHachsharaStore } from "../hooks/useHachsharaStore";
import { useHarelStore } from "../hooks/useHarelStore";
import { useClalHealthStore } from "../hooks/useClalHealthStore";
import { useMenoraStore } from "../hooks/useMenoraStore";

const S = {
  overlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(15,23,42,0.8)", backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 9999, padding: 20, direction: "rtl", fontFamily: "system-ui, sans-serif"
  },
  modal: {
    background: "white", borderRadius: 24, width: "100%", maxWidth: 850,
    maxHeight: "90vh", display: "flex", flexDirection: "column",
    boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)", overflow: "hidden"
  },
  header: {
    padding: "24px 32px", background: "linear-gradient(135deg, #334155 0%, #0f172a 100%)",
    color: "white", display: "flex", justifyContent: "space-between", alignItems: "center"
  },
  content: {
    padding: 32, overflowY: "auto", flex: 1, minHeight: 0, background: "#f8fafc",
    display: "flex", flexDirection: "column", gap: 24
  },
  monthGroup: {
    background: "white", borderRadius: 16, border: "1px solid #e2e8f0", overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)", flexShrink: 0
  },
  monthHeader: {
    background: "#f1f5f9", padding: "16px 24px", fontWeight: 700, color: "#334155",
    borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center"
  },
  fileRow: {
    padding: "16px 24px", borderBottom: "1px solid #f1f5f9", display: "flex",
    justifyContent: "space-between", alignItems: "center", transition: "background 0.2s"
  },
  badge: {
    padding: "4px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, display: "inline-flex", gap: 4
  },
  btnDanger: {
    background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: 8, padding: "6px 12px",
    fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.2s"
  }
};

export default function FilesManagerModal({ onClose, onOpenUpload }) {
  const { clients, removeDatasetByFileName } = useClientStore();
  const { holdingsClients, removeHoldingsFile, cleanStaleMonths } = useHoldingsStore();
  const { imports: hachsharaImports, removeImportByFileName: removeHachsharaByFileName } = useHachsharaStore();
  const { imports: harelImports, removeImportByFileName: removeHarelByFileName } = useHarelStore();
  const { clalImports, removeClalImport } = useClalHealthStore();
  const { imports: menoraImports, removeImportByFileName: removeMenoraByFileName } = useMenoraStore();

  const [searchTerm, setSearchTerm] = useState("");
  const [filterMonth, setFilterMonth] = useState("all");

  const adviceFiles = useMemo(() => {
    const map = new Map();
    for (const c of clients) {
      for (const ds of (c.datasets || [])) {
        if (!ds.fileInfo?.fileName) continue;
        const key = ds.fileInfo.fileName;
        if (!map.has(key)) {
           map.set(key, {
             type: "advice",
             fileName: ds.fileInfo.fileName,
             taarichBitzua: ds.fileInfo.taarichBitzua || ds.taarichBitzua,
             addedAt: ds.addedAt,
             sourceType: ds.fileInfo.sourceType || SOURCE_TYPE.MASLEKA,
             clientsCount: new Set(),
             fundsCount: 0
           });
        }
        const entry = map.get(key);
        entry.clientsCount.add(c.id);
        entry.fundsCount += (ds.funds || []).length;
      }
    }
    return Array.from(map.values()).map(e => ({ ...e, clientsCount: e.clientsCount.size }));
  }, [clients]);

  const holdingsFiles = useMemo(() => {
    const map = new Map();
    for (const c of holdingsClients) {
      for (const s of (c.sources || [])) {
        if (!s.fileName) continue;
        const key = s.fileName;
        if (!map.has(key)) {
          map.set(key, {
             type: "holdings",
             fileName: s.fileName,
             taarichBitzua: s.taarichBitzua,
             addedAt: s.addedAt,
             companyCode: s.companyCode,
             shemYatzran: s.shemYatzran || null,
             sourceType: s.sourceType || (String(s.companyCode || "").startsWith("MP_") ? SOURCE_TYPE.MANAGED : SOURCE_TYPE.MASLEKA),
             clientsCount: new Set(),
             fundsCount: 0
          });
        }
        const entry = map.get(key);
        entry.clientsCount.add(c.id);
        entry.fundsCount += (c.funds || []).filter(f => f._sourceFileName === s.fileName).length;
        // fallback לקבצים שיובאו לפני שנשמר shemYatzran — שם החברה מהקרנות עצמן
        if (!entry.shemYatzran) {
          const f = (c.funds || []).find(f => f._sourceFileName === s.fileName && f.companyName);
          if (f) entry.shemYatzran = f.companyName;
        }
      }
    }
    return Array.from(map.values()).map(e => ({ ...e, clientsCount: e.clientsCount.size }));
  }, [holdingsClients]);

  const hachsharaFiles = useMemo(() => {
    return hachsharaImports.map(imp => ({
      type: "hachshara",
      fileName: imp.fileName,
      taarichBitzua: imp.reportDate,
      addedAt: imp.importedAt,
      companyCode: imp.companyCode,
      sourceType: imp.sourceType || SOURCE_TYPE.PRAT,
      clientsCount: imp.members?.length || 0,
      fundsCount: imp.members?.reduce((s, m) => s + (m.funds?.length || 0), 0) || 0
    }));
  }, [hachsharaImports]);

  const harelFiles = useMemo(() => {
    return harelImports.map(imp => ({
      type: "harel",
      fileName: imp.fileName,
      taarichBitzua: imp.reportDate,
      addedAt: imp.importedAt,
      sourceType: imp.sourceType || SOURCE_TYPE.PRAT,
      clientsCount: imp.members?.length || 0,
      fundsCount: imp.members?.reduce((s, m) => s + (m.funds?.length || 0), 0) || 0
    }));
  }, [harelImports]);

  const clalHealthFiles = useMemo(() => {
    return clalImports.map(imp => ({
      type: "clalHealth",
      kind: imp.kind || "health",
      fileName: imp.fileName,
      taarichBitzua: imp.reportDate,
      addedAt: imp.addedAt,
      sourceType: imp.sourceType || SOURCE_TYPE.PRAT,
      shemYatzran: imp.kind === "life" ? "כלל ביטוח חיים" : "כלל בריאות",
      clientsCount: new Set(imp.members.map(m => m.taz)).size,
      fundsCount: imp.members.reduce((s, m) => s + (m.funds?.length || 0), 0),
    }));
  }, [clalImports]);

  const menoraFiles = useMemo(() => {
    return menoraImports.map(imp => ({
      type: "menora",
      fileName: imp.fileName,
      taarichBitzua: imp.reportDate,
      addedAt: imp.importedAt,
      sourceType: imp.sourceType || SOURCE_TYPE.PRAT,
      clientsCount: imp.members?.length || 0,
      fundsCount: imp.members?.reduce((s, m) => s + (m.funds?.length || 0), 0) || 0
    }));
  }, [menoraImports]);

  const allFiles = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return [...adviceFiles, ...holdingsFiles, ...hachsharaFiles, ...harelFiles, ...clalHealthFiles, ...menoraFiles]
      .filter(f =>
        f.fileName.toLowerCase().includes(term) ||
        (f.shemYatzran || "").toLowerCase().includes(term)
      )
      .sort((a, b) => new Date(b.addedAt || 0) - new Date(a.addedAt || 0));
  }, [adviceFiles, holdingsFiles, hachsharaFiles, harelFiles, clalHealthFiles, menoraFiles, searchTerm]);

  const groupedByMonth = useMemo(() => {
    const groups = {};
    for (const f of allFiles) {
      // Use taarichBitzua for grouping to represent "Value Month" (fallback to addedAt)
      const dateStr = f.taarichBitzua || f.addedAt;
      if (!dateStr) continue;
      
      let key = "";
      if (typeof dateStr === "string" && /^\d{2}[/\-]\d{2}[/\-]\d{4}$/.test(dateStr)) {
        // Handle DD/MM/YYYY or DD-MM-YYYY format
        const parts = dateStr.split(/[/\-]/);
        key = `${parts[2]}-${parts[1]}`;
      } else {
        const d = new Date(dateStr);
        if (isNaN(d)) continue;
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      }
      
      if (!key) continue;
      if (!groups[key]) groups[key] = [];
      groups[key].push(f);
    }
    return Object.entries(groups).sort((a,b) => b[0].localeCompare(a[0]));
  }, [allFiles]);

  const visibleGroups = useMemo(() => {
    if (filterMonth === "all") return groupedByMonth;
    return groupedByMonth.filter(([m]) => m === filterMonth);
  }, [groupedByMonth, filterMonth]);

  const handleDelete = (f) => {
    const displayName = f.shemYatzran
      ? `${f.shemYatzran}${f.taarichBitzua ? ` (${String(f.taarichBitzua).split("T")[0]})` : ""}`
      : f.fileName;
    if (!window.confirm(`האם אתה בטוח שברצונך להסיר את: ${displayName}?\nהפעולה תמחק את כל הקרנות/לקוחות שנוצרו דרכו.`)) return;

    // Keep the modal open — defer the heavy sync work so the confirm dialog
    // closes first, then the file disappears from the list (stores update reactively).
    requestAnimationFrame(() => setTimeout(() => {
      try {
        if (f.type === "advice") removeDatasetByFileName(f.fileName);
        else if (f.type === "holdings") removeHoldingsFile(f.fileName);
        else if (f.type === "hachshara") removeHachsharaByFileName(f.fileName);
        else if (f.type === "harel") removeHarelByFileName(f.fileName);
        else if (f.type === "clalHealth") removeClalImport(f.fileName);
        else if (f.type === "menora") removeMenoraByFileName(f.fileName);
      } catch (err) {
        alert("שגיאה במחיקת הקובץ: " + err.message + "\n" + err.stack);
        console.error("Delete Error:", err);
      }
    }, 0));
  };

  const handleCleanStaleMonths = () => {
    if (!window.confirm("לנקות חודשים שגויים בהיסטוריית האחזקות?\nיוסרו רק חודשים המאוחרים מתאריך הנכונות האמיתי של הנתונים (ארטיפקטים של תאריך הפקה/תיבה). חודשים לגיטימיים יישמרו.")) return;
    try {
      const res = cleanStaleMonths();
      if (res.monthsRemoved > 0) {
        alert(`נוקו ${res.monthsRemoved} חודשים שגויים אצל ${res.clientsAffected} לקוחות.\nחודשים שהוסרו: ${res.periods.join(", ")}`);
      } else {
        alert("לא נמצאו חודשים שגויים לניקוי. 👍");
      }
    } catch (err) {
      alert("שגיאה בניקוי חודשים: " + err.message);
      console.error("Clean stale months error:", err);
    }
  };

  const handleClearMonth = (monthKey, filesInMonth) => {
    if (!window.confirm(`האם להסיר את כל ${filesInMonth.length} הקבצים מחודש ${monthKey}? הפעולה תמחק אותם לחלוטין.`)) return;
    // Keep the modal open; remove reactively in the background.
    requestAnimationFrame(() => setTimeout(() => {
      try {
        filesInMonth.forEach(f => {
          if (f.type === "advice") removeDatasetByFileName(f.fileName);
          else if (f.type === "holdings") removeHoldingsFile(f.fileName);
          else if (f.type === "hachshara") removeHachsharaByFileName(f.fileName);
          else if (f.type === "harel") removeHarelByFileName(f.fileName);
          else if (f.type === "clalHealth") removeClalImport(f.fileName);
          else if (f.type === "menora") removeMenoraByFileName(f.fileName);
        });
      } catch (err) {
        alert("שגיאה במחיקת החודש: " + err.message + "\n" + err.stack);
        console.error("Clear Month Error:", err);
      }
    }, 0));
  };

  const getTypeStyle = (type, companyCode, fileName, kind) => {
    if (type === "advice") return { bg: "#dbeafe", color: "#1e3a8a", icon: "📋", label: "טרום ייעוץ" };
    if (type === "holdings") {
      if (String(companyCode || "").startsWith("MP_")) {
        return { bg: "#f0f9ff", color: "#0369a1", icon: "📈", label: "ניהול תיקים" };
      }
      if (companyCode === "10" || (fileName && (fileName.toLowerCase().includes("life") || fileName.includes("מגדל")))) {
        return { bg: "#e0e7ff", color: "#3730a3", icon: "🏢", label: "מגדל (פרט ובריאות)" };
      }
      return { bg: "#fef3c7", color: "#92400e", icon: "📦", label: "אחזקות (מבנה 008)" };
    }
    if (type === "hachshara") return { bg: "#fce7f3", color: "#9d174d", icon: "🏢", label: "הכשרה" };
    if (type === "harel") return { bg: "#fef3c7", color: "#92400e", icon: "🦁", label: "הראל" };
    if (type === "clalHealth") {
      if (kind === "life") return { bg: "#eff6ff", color: "#1d4ed8", icon: "🛡️", label: "כלל ביטוח חיים (אפקס)" };
      return { bg: "#f5f3ff", color: "#6d28d9", icon: "🩺", label: "כלל בריאות (אפקס)" };
    }
    if (type === "menora") return { bg: "#f0fdfa", color: "#0f766e", icon: "🏢", label: "מנורה" };
    return { bg: "#f1f5f9", color: "#475569", icon: "📄", label: "אחר" };
  };

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <div style={S.header}>
          <div>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>ניהול מקורות מידע</h2>
            <div style={{ fontSize: 14, opacity: 0.9, marginTop: 4 }}>
              צפה והסר קבצים שהועלו למערכת
            </div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"white", fontSize: 28, cursor:"pointer" }}>×</button>
        </div>

        <div style={{ padding: "16px 32px", background: "white", borderBottom: "1px solid #e2e8f0", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          {onOpenUpload && (
            <button
              onClick={onOpenUpload}
              style={{
                padding: "10px 18px", background: "#4338ca", color: "white",
                border: "none", borderRadius: 8, cursor: "pointer",
                fontWeight: 700, fontSize: 13, whiteSpace: "nowrap",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >📤 העלה קבצים</button>
          )}
          <button
            onClick={handleCleanStaleMonths}
            title="מסיר חודשים שגויים בהיסטוריית האחזקות (מאוחרים מתאריך הנכונות האמיתי)"
            style={{
              padding: "10px 16px", background: "#fef3c7", color: "#92400e",
              border: "1px solid #fde68a", borderRadius: 8, cursor: "pointer",
              fontWeight: 700, fontSize: 13, whiteSpace: "nowrap",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >🧹 נקה חודשים שגויים</button>
          <input
            type="text"
            placeholder="🔍 חיפוש קובץ..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ flex: 1, minWidth: 180, padding: "10px 16px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14, outline: "none" }}
          />
          <select
            value={filterMonth}
            onChange={e => setFilterMonth(e.target.value)}
            style={{ minWidth: 140, padding: "10px 16px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14, outline: "none", background: "white", color: "#334155", fontWeight: 600 }}
          >
            <option value="all">📅 כל החודשים</option>
            {groupedByMonth.map(([m]) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        <div style={S.content}>
          {visibleGroups.length === 0 ? (
            <div style={{ textAlign: "center", color: "#94a3b8", padding: 40, fontSize: 16 }}>
              לא נמצאו קבצים מנוהלים במערכת.
            </div>
          ) : (
            visibleGroups.map(([monthKey, files]) => (
              <div key={monthKey} style={S.monthGroup}>
                <div style={S.monthHeader}>
                  <div style={{ fontSize: 16 }}>
                    חודש ערך: <span style={{ color: "#2563eb" }}>{monthKey}</span> 
                    <span style={{ fontSize: 13, color: "#64748b", marginRight: 8 }}>({files.length} קבצים)</span>
                  </div>
                  <button style={S.btnDanger} onClick={() => handleClearMonth(monthKey, files)}>
                    🗑️ נקה את כל חודש {monthKey}
                  </button>
                </div>
                <div>
                  {files.map((f, i) => {
                    const style = getTypeStyle(f.type, f.companyCode, f.fileName, f.kind);
                    return (
                      <div key={f.fileName + i} style={S.fileRow} onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 14 }}>
                            {f.shemYatzran
                              ? <>🏢 {f.shemYatzran}{f.taarichBitzua ? ` · ${String(f.taarichBitzua).split("T")[0]}` : ""}</>
                              : f.fileName}
                          </div>
                          {f.shemYatzran && (
                            <div style={{ fontSize: 11, color: "#94a3b8", direction: "ltr", textAlign: "right" }}>{f.fileName}</div>
                          )}
                          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            {(() => {
                              const sm = sourceMeta(f.sourceType);
                              return sm ? (
                                <span title={`מקור מידע: ${sm.label}`} style={{ ...S.badge, background: sm.bg, color: sm.color, border: `1px solid ${sm.color}22` }}>
                                  <span>{sm.icon}</span> {sm.label}
                                </span>
                              ) : null;
                            })()}
                            <span style={{ ...S.badge, background: style.bg, color: style.color }}>
                              <span>{style.icon}</span> {style.label}
                            </span>
                            <span style={{ fontSize: 12, color: "#64748b" }}>
                              {f.clientsCount} לקוחות | {f.fundsCount} פוליסות
                            </span>
                            {f.taarichBitzua && (
                              <span style={{ fontSize: 12, color: "#64748b" }}>
                                📅 ערך: {f.taarichBitzua.split('T')[0]}
                              </span>
                            )}
                          </div>
                        </div>
                        <button style={S.btnDanger} onClick={() => handleDelete(f)}>
                          הסר קובץ
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
